import { DestroyRef, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Address, PointData } from '@checkout/const';
import { DeliveryAddressService } from '@checkout/feature';
import { GeocodeResult } from '@yandex-maps/const';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import { concatMap, delay, filter, map, take } from 'rxjs/operators';

export type AddressAdditionalData = Pick<
  Address,
  'country' | 'regionName' | 'district' | 'town' | 'streetname' | 'streetnumber' | 'point'
>;

export interface AddressData {
  address: string;
  appartment: string;
  entrance: string;
  floor: number;
  heightLimit: number;
  hasHeightLimit: boolean;
  domofon: string;
  privateHouse: boolean;
  cargoLift: boolean;
  passengerLift: boolean;
  addressAdditionalData?: AddressAdditionalData;
}

export interface GeoObject {
  geocodeResult: GeocodeResult;
  exactPoint?: PointData;
}

@Injectable()
export class DeliveryAddressDialogComponentService implements OnDestroy {
  form: FormGroup = new FormGroup({
    address: new FormControl(''),
    appartment: new FormControl(),
    entrance: new FormControl(),
    floor: new FormControl(),
    heightLimit: new FormControl(),
    hasHeightLimit: new FormControl(),
    domofon: new FormControl(),
    privateHouse: new FormControl(),
    passengerLift: new FormControl(),
    cargoLift: new FormControl(),

    // Невидимые поля
    addressAdditionalData: new FormControl(null, Validators.required),
  });
  addressData$: Observable<AddressData>;
  activeAddressId$: Observable<string>;
  mapIsShown$ = new BehaviorSubject<boolean>(false);
  suggestedCoords$ = new BehaviorSubject<ymaps.Coordinate>(undefined);

  private geoObject$ = new BehaviorSubject<GeoObject>(null);

  constructor(
    private deliveryAddressService: DeliveryAddressService,
    private destroyRef: DestroyRef,
  ) {
    this.form.get('address').addValidators((control) => {
      const geoObject = this.geoObject$.value;

      if (
        !control.value ||
        !geoObject ||
        geoObject.geocodeResult.getAddressLine() !== control.value ||
        (!geoObject.exactPoint && !geoObject.geocodeResult.getPremiseNumber())
      ) {
        return { required: true };
      }

      return null;
    });

    this.geoObject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.form.get('address').updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });

    this.addressData$ = merge(
      this.deliveryAddressService.editableAddressId$.pipe(
        concatMap((addressId) => {
          if (addressId) {
            return this.getExistingAddressData(addressId);
          }

          return of(this.getEmptyAddressData());
        }),
        take(1),
        delay(0),
      ),
      this.geoObject$.pipe(
        filter((geoObject) => !!geoObject),
        map(({ geocodeResult, exactPoint }) => ({
          ...(<AddressData>this.form.value),
          address: geocodeResult.getAddressLine(),
          addressAdditionalData: {
            country: {
              isocode: geocodeResult.getCountryCode(),
            },
            regionName: geocodeResult.getAdministrativeAreas()[1],
            district: geocodeResult.getArea(),
            town: geocodeResult.getLocalities()[0],
            streetname: geocodeResult.getThoroughfare(),
            streetnumber: geocodeResult.getPremiseNumber(),
            point: exactPoint
              ? exactPoint
              : {
                  latitude: geocodeResult.geometry.getCoordinates()[0],
                  longitude: geocodeResult.geometry.getCoordinates()[1],
                },
          },
        })),
      ),
    );

    this.activeAddressId$ = this.deliveryAddressService.editableAddressId$.asObservable();
  }

  ngOnDestroy(): void {
    this.deliveryAddressService.editableAddressId$.next(undefined);
  }

  setGeoObject(geoObject: GeoObject): void {
    this.geoObject$.next(geoObject);
  }

  pintMapToCoord(coordinates: ymaps.Coordinate): void {
    this.suggestedCoords$.next(coordinates);
  }

  toggleMap(isShown: boolean = !this.mapIsShown$.value): void {
    this.mapIsShown$.next(isShown);
  }

  closeMap(): void {
    this.toggleMap(false);
  }

  saveAddress(data: AddressData): void {
    this.deliveryAddressService.editableAddressId$.pipe(take(1)).subscribe((addressId) => {
      const checkoutAddress: Address = {
        country: data.addressAdditionalData.country,
        regionName: data.addressAdditionalData.regionName,
        district: data.addressAdditionalData.district,
        town: data.addressAdditionalData.town,
        streetname: data.addressAdditionalData.streetname,
        streetnumber: data.addressAdditionalData.streetnumber,
        point: data.addressAdditionalData.point,
        appartment: data.appartment,
        entrance: data.entrance,
        domofon: data.domofon,
        floor: data.floor,
        heightLimit: (data.hasHeightLimit && data.heightLimit) || undefined,
        formattedAddress: data.address,
        id: addressId,
        privateHouse: data.privateHouse,
        passengerLift: data.passengerLift,
        cargoLift: data.cargoLift,
      };
      if (addressId) {
        this.deliveryAddressService.editSavedAddress(checkoutAddress);
      } else {
        this.deliveryAddressService.saveAddress(checkoutAddress);
      }
      this.deliveryAddressService.closeAddressModal();
    });
  }

  private getExistingAddressData(addressId: string): Observable<AddressData> {
    return this.deliveryAddressService.getAddress(addressId).pipe(
      filter<Address>(Boolean),
      map((address) => this.mapAddress(address)),
    );
  }

  private mapAddress(address: Partial<Address>): AddressData {
    return {
      address: address.formattedAddress,
      appartment: address.appartment || null,
      entrance: address.entrance || null,
      domofon: address.domofon || null,
      floor: address.floor || null,
      hasHeightLimit: !!address.heightLimit,
      heightLimit: address.heightLimit ? address.heightLimit : null,
      privateHouse: address.privateHouse || null,
      passengerLift: address.passengerLift || null,
      cargoLift: address.cargoLift || null,
      addressAdditionalData: address.country
        ? {
            country: address.country,
            regionName: address.regionName,
            district: address.district,
            town: address.town,
            streetname: address.streetname,
            streetnumber: address.streetnumber,
            point: address.point,
          }
        : null,
    };
  }

  private getEmptyAddressData(): AddressData {
    return {
      address: '',
      appartment: null,
      entrance: null,
      floor: null,
      heightLimit: null,
      hasHeightLimit: false,
      domofon: null,
      privateHouse: false,
      cargoLift: false,
      passengerLift: false,
      addressAdditionalData: null,
    };
  }
}
